/**
 * Utility functions for working with TrendLine Strategy API
 */

/**
 * Generate a unique ID for rule groups and conditions
 * @param {string} prefix - Prefix for the ID (e.g., 'group', 'condition')
 * @returns {string} Unique ID
 */
export const generateId = (prefix = 'id') => {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Create an empty rule group with default values
 * @param {'BUY'|'SELL'|'NONE'} action - The action for this group
 * @param {string} [name] - Optional group name
 * @returns {Object} Empty rule group
 */
export const createEmptyRuleGroup = (action = 'BUY', name = '') => ({
  groupId: generateId('group'),
  groupName: name || (action === 'BUY' ? 'Entry Conditions' : 'Exit Conditions'),
  operator: 'AND',
  action,
  conditions: [],
});

/**
 * Create an empty indicator condition
 * @param {string} indicatorType - Type of indicator (RSI, SMA, EMA, MACD)
 * @returns {Object} Empty indicator condition
 */
export const createIndicatorCondition = (indicatorType = 'RSI') => ({
  conditionType: 'INDICATOR',
  indicatorType,
  operator: 'LESS_THAN',
  parameters: getDefaultIndicatorParameters(indicatorType),
});

/**
 * Create an empty trendline condition
 * @param {Object} [trendLine] - Optional trendline data
 * @returns {Object} Empty trendline condition
 */
export const createTrendLineCondition = (trendLine = null) => ({
  conditionType: 'TRENDLINE',
  trendLine: trendLine || {
    name: '',
    id: generateId('trendline'),
    x1: null,
    y1: null,
    x2: null,
    y2: null,
  },
  triggerType: 'PRICE_TOUCHED_TREND_LINE',
});

/**
 * Create an empty candlestick condition
 * @param {string} patternType - Pattern type (e.g., 'DOJI', 'HAMMER')
 * @returns {Object} Empty candlestick condition
 */
export const createCandlestickCondition = (patternType = 'DOJI') => ({
  conditionType: 'CANDLESTICK',
  patternType,
});

/**
 * Create an empty composite condition (indicator vs indicator)
 * @param {string} leftType - Left indicator type (e.g., 'EMA')
 * @param {string} rightType - Right indicator type (e.g., 'SMA')
 * @param {string} operator - Comparison operator (e.g., 'CROSSED_UP')
 * @returns {Object} Empty composite condition
 */
export const createCompositeCondition = (leftType = 'EMA', rightType = 'SMA', operator = 'CROSSED_UP') => ({
  conditionType: 'COMPOSITE',
  operator,
  leftIndicator: {
    type: leftType,
    indicatorType: leftType,
    parameters: getDefaultIndicatorParameters(leftType),
  },
  rightIndicator: {
    type: rightType,
    indicatorType: rightType,
    parameters: getDefaultIndicatorParameters(rightType),
  },
});

/**
 * Create an empty vote condition (N-of-M confluence)
 * @param {number} requiredVotes - Minimum conditions that must be true
 * @returns {Object} Empty vote condition
 */
export const createVoteCondition = (requiredVotes = 2) => ({
  conditionType: 'VOTE',
  requiredVotes,
  conditions: [],
});

/**
 * Create an empty chain condition (sequential pattern)
 * @returns {Object} Empty chain condition
 */
export const createChainCondition = () => ({
  conditionType: 'CHAIN',
  initialCondition: null,
  chainLinks: [],
});

/**
 * Get default parameters for an indicator type
 * @param {string} indicatorType - Type of indicator
 * @returns {Object} Default parameters
 */
export const getDefaultIndicatorParameters = (indicatorType) => {
  switch (indicatorType) {
    case 'RSI':
      return { period: 14, threshold: 30.0 };
    case 'SMA':
      return { period: 20, threshold: null };
    case 'EMA':
      return { period: 20, threshold: null };
    case 'MACD':
      return { fastPeriod: 12, slowPeriod: 26, signalPeriod: 9, threshold: 0 };
    default:
      return {};
  }
};

/**
 * Create an empty strategy with default structure
 * @param {string} [name] - Strategy name
 * @returns {Object} Empty strategy
 */
export const createEmptyStrategy = (name = '') => ({
  name,
  description: '',
  tickerName: '',
  interval: '1h', // Default candle interval
  startDate: null,
  endDate: null,
  capitalAmount: null,
  lotSize: null, // Optional — defaults to 1 on backend if not provided
  tradingType: 'INTRADAY',
  orderType: 'MARKET_ORDER',
  riskManagement: null, // { stopLossPercent, targetProfitPercent, trailingStopPercent }
  ruleGroups: [],
  public: false,
});

/**
 * Format date for API (ISO string format)
 * @param {Date|string} date - Date to format
 * @returns {string|null} ISO date string or null
 */
export const formatDateForApi = (date) => {
  if (!date) return null;
  if (typeof date === 'string') return date;
  if (date instanceof Date) {
    return date.toISOString().split('T')[0]; // Returns "YYYY-MM-DD"
  }
  return null;
};

/**
 * Parse date array from API response to Date object
 * API returns dates as [year, month, day] arrays
 * @param {Array|string} dateValue - Date from API
 * @returns {Date|null} Date object or null
 */
export const parseDateFromApi = (dateValue) => {
  if (!dateValue) return null;
  if (typeof dateValue === 'string') return new Date(dateValue);
  if (Array.isArray(dateValue) && dateValue.length === 3) {
    // API returns [year, month, day] - month is 1-indexed
    return new Date(dateValue[0], dateValue[1] - 1, dateValue[2]);
  }
  return null;
};

/**
 * Convert timestamp to epoch milliseconds
 * @param {Date|number|string} value - Value to convert
 * @returns {number|null} Epoch milliseconds
 */
export const toEpochMillis = (value) => {
  if (!value) return null;
  if (typeof value === 'number') return value;
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'string') return new Date(value).getTime();
  return null;
};

/**
 * Convert epoch milliseconds to Date
 * @param {number} epochMillis - Epoch milliseconds
 * @returns {Date|null} Date object
 */
export const fromEpochMillis = (epochMillis) => {
  if (!epochMillis) return null;
  return new Date(epochMillis);
};

/**
 * Validate a strategy object before submission
 * @param {Object} strategy - Strategy to validate
 * @returns {Object} { valid: boolean, errors: string[] }
 */
export const validateStrategyLocally = (strategy) => {
  const errors = [];

  if (!strategy.name || strategy.name.trim().length < 3) {
    errors.push('Strategy name must be at least 3 characters');
  }

  if (!strategy.tickerName || strategy.tickerName.trim().length === 0) {
    errors.push('Ticker symbol is required');
  }

  if (strategy.capitalAmount !== null && strategy.capitalAmount !== undefined) {
    if (strategy.capitalAmount < 1000) {
      errors.push('Capital Amount must be at least ₹1,000');
    }
    if (strategy.capitalAmount > 10000000) {
      errors.push('Capital Amount cannot exceed ₹1,00,00,000');
    }
  }

  if (!strategy.tradingType) {
    errors.push('Trading Type is required');
  }

  if (!strategy.orderType) {
    errors.push('Order Type is required');
  }

  if (!strategy.ruleGroups || strategy.ruleGroups.length === 0) {
    errors.push('At least one rule group is required');
  } else {
    // Check for at least one BUY and one SELL action
    const hasBuy = strategy.ruleGroups.some(g => g.action === 'BUY');
    const hasSell = strategy.ruleGroups.some(g => g.action === 'SELL');

    if (!hasBuy) {
      errors.push('At least one rule group with BUY action is recommended');
    }
    if (!hasSell) {
      errors.push('At least one rule group with SELL action is recommended');
    }

    // Validate each rule group
    strategy.ruleGroups.forEach((group, groupIndex) => {
      if (!group.conditions || group.conditions.length === 0) {
        errors.push(`Rule group ${groupIndex + 1} must have at least one condition`);
      }

      group.conditions?.forEach((condition, condIndex) => {
        if (condition.conditionType === 'TRENDLINE') {
          if (!condition.trendLine || !condition.trendLine.x1 || !condition.trendLine.y1) {
            errors.push(`Rule group ${groupIndex + 1}, condition ${condIndex + 1}: Trend line coordinates are required`);
          }
        } else if (condition.conditionType === 'INDICATOR') {
          if (!condition.indicatorType) {
            errors.push(`Rule group ${groupIndex + 1}, condition ${condIndex + 1}: Indicator type is required`);
          }
        } else if (condition.conditionType === 'COMPOSITE') {
          if (!condition.leftIndicator?.type && !condition.leftIndicator?.indicatorType) {
            errors.push(`Rule group ${groupIndex + 1}, condition ${condIndex + 1}: Left indicator type is required`);
          }
          if (!condition.rightIndicator?.type && !condition.rightIndicator?.indicatorType) {
            errors.push(`Rule group ${groupIndex + 1}, condition ${condIndex + 1}: Right indicator type is required`);
          }
          if (!condition.operator) {
            errors.push(`Rule group ${groupIndex + 1}, condition ${condIndex + 1}: Comparison operator is required`);
          }
        } else if (condition.conditionType === 'CANDLESTICK') {
          if (!condition.patternType) {
            errors.push(`Rule group ${groupIndex + 1}, condition ${condIndex + 1}: Candlestick pattern type is required`);
          }
        } else if (condition.conditionType === 'VOTE') {
          if (!condition.conditions || condition.conditions.length < 2) {
            errors.push(`Rule group ${groupIndex + 1}, condition ${condIndex + 1}: Vote condition requires at least 2 sub-conditions`);
          }
          if (!condition.requiredVotes || condition.requiredVotes < 1) {
            errors.push(`Rule group ${groupIndex + 1}, condition ${condIndex + 1}: Required votes must be at least 1`);
          }
          if (condition.requiredVotes > (condition.conditions?.length || 0)) {
            errors.push(`Rule group ${groupIndex + 1}, condition ${condIndex + 1}: Required votes cannot exceed number of conditions`);
          }
        } else if (condition.conditionType === 'CHAIN') {
          if (!condition.initialCondition) {
            errors.push(`Rule group ${groupIndex + 1}, condition ${condIndex + 1}: Chain condition requires an initial condition`);
          }
          if (!condition.chainLinks || condition.chainLinks.length === 0) {
            errors.push(`Rule group ${groupIndex + 1}, condition ${condIndex + 1}: Chain condition requires at least one chain link`);
          }
          condition.chainLinks?.forEach((link, linkIdx) => {
            if (!link.condition) {
              errors.push(`Rule group ${groupIndex + 1}, condition ${condIndex + 1}, link ${linkIdx + 1}: Chain link condition is required`);
            }
            if (!link.threshold || link.threshold < 1) {
              errors.push(`Rule group ${groupIndex + 1}, condition ${condIndex + 1}, link ${linkIdx + 1}: Threshold must be at least 1 bar`);
            }
          });
        }
      });
    });
  }

  return {
    valid: errors.length === 0,
    errors,
  };
};

/**
 * Transform a single condition to API format (used recursively for Vote/Chain sub-conditions)
 * @param {Object} condition - Condition to transform
 * @returns {Object} API-ready condition
 */
const transformConditionToApi = (condition) => {
  if (condition.conditionType === 'TRENDLINE') {
    return {
      conditionType: 'TRENDLINE',
      trendLine: {
        name: condition.trendLine.name,
        id: condition.trendLine.id,
        x1: toEpochMillis(condition.trendLine.x1),
        y1: condition.trendLine.y1,
        x2: toEpochMillis(condition.trendLine.x2),
        y2: condition.trendLine.y2,
      },
      triggerType: condition.triggerType,
    };
  } else if (condition.conditionType === 'COMPOSITE') {
    return {
      conditionType: 'COMPOSITE',
      operator: condition.operator,
      leftIndicator: condition.leftIndicator ? {
        indicatorType: condition.leftIndicator.type || condition.leftIndicator.indicatorType,
        parameters: condition.leftIndicator.parameters || {},
      } : null,
      rightIndicator: condition.rightIndicator ? {
        indicatorType: condition.rightIndicator.type || condition.rightIndicator.indicatorType,
        parameters: condition.rightIndicator.parameters || {},
      } : null,
    };
  } else if (condition.conditionType === 'CANDLESTICK') {
    return { conditionType: 'CANDLESTICK', patternType: condition.patternType };
  } else if (condition.conditionType === 'VOTE') {
    return {
      conditionType: 'VOTE',
      requiredVotes: condition.requiredVotes,
      conditions: (condition.conditions || []).map(transformConditionToApi),
    };
  } else if (condition.conditionType === 'CHAIN') {
    return {
      conditionType: 'CHAIN',
      initialCondition: condition.initialCondition ? transformConditionToApi(condition.initialCondition) : null,
      chainLinks: (condition.chainLinks || []).map(link => ({
        condition: link.condition ? transformConditionToApi(link.condition) : null,
        threshold: link.threshold,
      })),
    };
  }
  return {
    conditionType: 'INDICATOR',
    indicatorType: condition.indicatorType,
    operator: condition.operator,
    parameters: condition.parameters,
  };
};

/**
 * Transform a single condition from API format (used recursively for Vote/Chain sub-conditions)
 * @param {Object} condition - API condition to transform
 * @returns {Object} Form-ready condition
 */
const transformConditionFromApi = (condition) => {
  if (condition.conditionType === 'TRENDLINE') {
    return {
      conditionType: 'TRENDLINE',
      trendLine: {
        name: condition.trendLine?.name,
        id: condition.trendLine?.id,
        x1: fromEpochMillis(condition.trendLine?.x1),
        y1: condition.trendLine?.y1,
        x2: fromEpochMillis(condition.trendLine?.x2),
        y2: condition.trendLine?.y2,
      },
      triggerType: condition.triggerType,
    };
  } else if (condition.conditionType === 'COMPOSITE') {
    return {
      conditionType: 'COMPOSITE',
      operator: condition.operator,
      leftIndicator: condition.leftIndicator ? {
        type: condition.leftIndicator.indicatorType,
        indicatorType: condition.leftIndicator.indicatorType,
        parameters: condition.leftIndicator.parameters || {},
      } : { type: '', parameters: {} },
      rightIndicator: condition.rightIndicator ? {
        type: condition.rightIndicator.indicatorType,
        indicatorType: condition.rightIndicator.indicatorType,
        parameters: condition.rightIndicator.parameters || {},
      } : { type: '', parameters: {} },
    };
  } else if (condition.conditionType === 'CANDLESTICK') {
    return { conditionType: 'CANDLESTICK', patternType: condition.patternType };
  } else if (condition.conditionType === 'VOTE') {
    return {
      conditionType: 'VOTE',
      requiredVotes: condition.requiredVotes,
      conditions: (condition.conditions || []).map(transformConditionFromApi),
    };
  } else if (condition.conditionType === 'CHAIN') {
    return {
      conditionType: 'CHAIN',
      initialCondition: condition.initialCondition ? transformConditionFromApi(condition.initialCondition) : null,
      chainLinks: (condition.chainLinks || []).map(link => ({
        condition: link.condition ? transformConditionFromApi(link.condition) : null,
        threshold: link.threshold,
      })),
    };
  }
  return {
    conditionType: 'INDICATOR',
    indicatorType: condition.indicatorType,
    operator: condition.operator,
    parameters: condition.parameters,
  };
};

/**
 * Transform strategy from form data to API format
 * @param {Object} formData - Form data
 * @returns {Object} API-ready strategy object
 */
export const transformToApiFormat = (formData) => {
  // Check if risk management provides exit rules
  const hasRiskManagementExitRules = formData.riskManagement && (
    formData.riskManagement.stopLossPercent ||
    formData.riskManagement.targetProfitPercent ||
    formData.riskManagement.trailingStopPercent
  );

  // Filter rule groups: remove empty SELL groups if risk management provides exit rules
  const filteredRuleGroups = formData.ruleGroups
    .filter(group => {
      // Keep BUY groups always
      if (group.action === 'BUY') return true;
      // For SELL groups: keep only if has conditions, OR if no risk management exit rules
      if (group.action === 'SELL') {
        const hasConditions = group.conditions && group.conditions.length > 0;
        return hasConditions || !hasRiskManagementExitRules;
      }
      return true;
    });

  return {
    name: formData.name,
    description: formData.description || '',
    tickerName: formData.tickerName?.toUpperCase(),
    interval: formData.interval || '1h',
    startDate: formatDateForApi(formData.startDate),
    endDate: formatDateForApi(formData.endDate),
    capitalAmount: formData.capitalAmount,
    lotSize: formData.lotSize != null ? parseInt(formData.lotSize, 10) : null,
    tradingType: formData.tradingType,
    orderType: formData.orderType,
    riskManagement: formData.riskManagement,
    public: formData.public,
    ruleGroups: filteredRuleGroups.map(group => ({
      groupId: group.groupId,
      groupName: group.groupName,
      operator: group.operator,
      action: group.action,
      conditions: group.conditions.map(condition => {
        if (condition.conditionType === 'TRENDLINE') {
          return {
            conditionType: 'TRENDLINE',
            trendLine: {
              name: condition.trendLine.name,
              id: condition.trendLine.id,
              x1: toEpochMillis(condition.trendLine.x1),
              y1: condition.trendLine.y1,
              x2: toEpochMillis(condition.trendLine.x2),
              y2: condition.trendLine.y2,
            },
            triggerType: condition.triggerType,
          };
        } else if (condition.conditionType === 'COMPOSITE') {
          // Transform COMPOSITE condition for API
          return {
            conditionType: 'COMPOSITE',
            operator: condition.operator,
            leftIndicator: condition.leftIndicator ? {
              indicatorType: condition.leftIndicator.type || condition.leftIndicator.indicatorType,
              parameters: condition.leftIndicator.parameters || {},
            } : null,
            rightIndicator: condition.rightIndicator ? {
              indicatorType: condition.rightIndicator.type || condition.rightIndicator.indicatorType,
              parameters: condition.rightIndicator.parameters || {},
            } : null,
          };
        } else if (condition.conditionType === 'CANDLESTICK') {
          return {
            conditionType: 'CANDLESTICK',
            patternType: condition.patternType,
          };
        } else if (condition.conditionType === 'VOTE') {
          return {
            conditionType: 'VOTE',
            requiredVotes: condition.requiredVotes,
            conditions: (condition.conditions || []).map(transformConditionToApi),
          };
        } else if (condition.conditionType === 'CHAIN') {
          return {
            conditionType: 'CHAIN',
            initialCondition: condition.initialCondition ? transformConditionToApi(condition.initialCondition) : null,
            chainLinks: (condition.chainLinks || []).map(link => ({
              condition: link.condition ? transformConditionToApi(link.condition) : null,
              threshold: link.threshold,
            })),
          };
        } else {
          return {
            conditionType: 'INDICATOR',
            indicatorType: condition.indicatorType,
            operator: condition.operator,
            parameters: condition.parameters,
          };
        }
      }),
    })),
  };
};

/**
 * Transform strategy from API format to form data
 * @param {Object} apiData - API response data
 * @returns {Object} Form-ready data
 */
export const transformFromApiFormat = (apiData) => {
  return {
    id: apiData.id,
    name: apiData.name,
    description: apiData.description || '',
    tickerName: apiData.tickerName,
    interval: apiData.interval || '1h',
    startDate: formatDateForApi(parseDateFromApi(apiData.startDate)), // YYYY-MM-DD string for date inputs
    endDate: formatDateForApi(parseDateFromApi(apiData.endDate)),     // YYYY-MM-DD string for date inputs
    capitalAmount: apiData.capitalAmount,
    lotSize: apiData.lotSize ?? null,
    tradingType: apiData.tradingType || 'INTRADAY',
    orderType: apiData.orderType || 'MARKET_ORDER',
    riskManagement: apiData.riskManagement,
    public: apiData.public || false,
    ruleGroups: apiData.ruleGroups?.map(group => ({
      groupId: group.groupId,
      groupName: group.groupName,
      operator: group.operator,
      action: group.action,
      conditions: group.conditions?.map(condition => {
        if (condition.conditionType === 'TRENDLINE') {
          return {
            conditionType: 'TRENDLINE',
            trendLine: {
              name: condition.trendLine?.name,
              id: condition.trendLine?.id,
              x1: fromEpochMillis(condition.trendLine?.x1),
              y1: condition.trendLine?.y1,
              x2: fromEpochMillis(condition.trendLine?.x2),
              y2: condition.trendLine?.y2,
            },
            triggerType: condition.triggerType,
          };
        } else if (condition.conditionType === 'COMPOSITE') {
          // Transform COMPOSITE condition from API
          return {
            conditionType: 'COMPOSITE',
            operator: condition.operator,
            leftIndicator: condition.leftIndicator ? {
              type: condition.leftIndicator.indicatorType,
              indicatorType: condition.leftIndicator.indicatorType,
              parameters: condition.leftIndicator.parameters || {},
            } : { type: '', parameters: {} },
            rightIndicator: condition.rightIndicator ? {
              type: condition.rightIndicator.indicatorType,
              indicatorType: condition.rightIndicator.indicatorType,
              parameters: condition.rightIndicator.parameters || {},
            } : { type: '', parameters: {} },
          };
        } else if (condition.conditionType === 'CANDLESTICK') {
          return {
            conditionType: 'CANDLESTICK',
            patternType: condition.patternType,
          };
        } else if (condition.conditionType === 'VOTE') {
          return {
            conditionType: 'VOTE',
            requiredVotes: condition.requiredVotes,
            conditions: (condition.conditions || []).map(transformConditionFromApi),
          };
        } else if (condition.conditionType === 'CHAIN') {
          return {
            conditionType: 'CHAIN',
            initialCondition: condition.initialCondition ? transformConditionFromApi(condition.initialCondition) : null,
            chainLinks: (condition.chainLinks || []).map(link => ({
              condition: link.condition ? transformConditionFromApi(link.condition) : null,
              threshold: link.threshold,
            })),
          };
        } else {
          return {
            conditionType: 'INDICATOR',
            indicatorType: condition.indicatorType,
            operator: condition.operator,
            parameters: condition.parameters,
          };
        }
      }) || [],
    })) || [],
    createdAt: parseDateFromApi(apiData.createdAt),
    updatedAt: parseDateFromApi(apiData.updatedAt),
  };
};
